/**
 * Regelversion 11: Eine lebendige Parteienlandschaft.
 *
 *  - Neugründungen: Einmal im Jahr prüft das Spiel, ob eine Lücke entstanden ist: Die Bevölkerung teilt eine
 *    Haltung, die keine Partei im Bundestag vertritt, eine Mutterpartei regiert mit und verbiegt sich, die
 *    Unzufriedenheit wächst. Dann kann sich eine neue Partei gründen (content/parties11.ts). Sie nimmt vor
 *    allem den Parteien mit ähnlichem Programm Stimmen ab.
 *  - Landtage: Bei jeder Landtagswahl bekommen alle Parteien ein Ergebnis im Land. Wer 5 % schafft, sitzt im
 *    Landtag. Neue Parteien gewinnen durch Landtagserfolge Schwung für den Bund.
 *  - Auflösung: Eine kleinere Partei löst sich auf, wenn sie zwei Bundestagswahlen in Folge unter 5 % bleibt,
 *    in keinem Landtag mehr sitzt und nirgends mitregiert. Ihre Wähler wandern zu ähnlichen Parteien.
 *  Die Parteien leben im Spielstand (S.px), damit ältere Partien unverändert bleiben (content/parties.ts, syncRegistry).
 *  Nur im Einzelspieler, nicht im Mehrspieler.
 */
import { S, log } from './state';
import { since } from './version';
import { rnd } from './rng';
import { clamp, de } from './util';
import { pub } from './opinion';
import { press } from './media';
import { approval } from './govern';
import { PARTIES, OUTLETS, BASE_PARTIES, syncRegistry } from '../content/parties';
import { AX, EQW } from '../content/axes';
import { AX3 } from '../content/axes3';
import { NEW_PARTIES, TYPE_N, type NewParty } from '../content/parties11';
import { STATES } from '../content/states';
import { PREF } from '../content/ressorts';
import { rivTick } from './rivals12';

export type Px = {
  parties: Record<string, any>; adj: Record<string, number>; born: Record<string, number>; gone: Record<string, number>;
  miss: Record<string, number>; lt: Record<string, { m: number; in: string[]; sh: Record<string, number> }>; next: number;
};
export const pxOn = (): boolean => since(11) && !S.mp;
export const px = (): Px => ((S as any).px ??= { parties: {}, adj: {}, born: {}, gone: {}, miss: {}, lt: {}, next: 6 });
export const applyPx = () => { syncRegistry(S.mp, (S as any).px); for (const N of NEW_PARTIES) if (N.pref && (S as any).px?.parties?.[N.id]) PREF[N.id] = N.pref; };

/** Höchstzahl der Computerparteien gleichzeitig. */
export const MAX_PARTIES = 8;

const active = (): string[] => Object.keys(PARTIES).filter((k) => !PARTIES[k].gone);
const posOf = (k: string): Record<string, number> => (k === 'YOU' ? S.you : PARTIES[k]?.pos || {});
/** Ähnlichkeit zweier Programme (0–1) auf den Kernthemen von `on` (sonst den großen Themen). */
function aff(a: string, b: string, on?: string[]): number {
  const ax = on && on.length ? on : ['sozial', 'haushalt', 'klima', 'migration', 'sicherheit'];
  const pa = posOf(a), pb = posOf(b);
  return Math.exp(-4 * ax.reduce((s, x) => s + Math.abs((pa[x] || 0) - (pb[x] || 0)), 0) / ax.length / 4);
}

/** Wie groß ist die Lücke für eine Partei (0 = keine, ab etwa .4 realistisch)? */
export function gapOf(N: NewParty): number {
  const core = Object.entries(N.core), tot = core.reduce((s, [, d]) => s + Math.abs(d), 0);
  let g = 0;
  if (tot > 0) {
    const sup = core.reduce((s, [a, d]) => s + Math.max(0, Math.sign(d) * pub(a)) * Math.abs(d), 0) / tot;
    const inB = (S.res?.inP || []).filter((k: string) => PARTIES[k] && !PARTIES[k].gone);
    const cov = Math.max(0, ...inB.map((k: string) => core.reduce((s, [a, d]) => s + Math.max(0, Math.sign(d) * (posOf(k)[a] || 0)) * Math.abs(d), 0) / tot / 2));
    g = sup / 2 - cov * .45;
  }
  const opp = (S as any).opp, appr = opp ? opp.appr : approval(), polar = (S as any).polar || 0;
  if (N.type === 'protest' || N.type === 'satire') g += Math.max(0, (46 - appr) / 18) + polar / 60;
  if (N.type === 'abspaltung' && N.parent?.some((p) => (opp ? opp.gov : S.partners).includes(p)) && S.m - S.m0 >= 24) g += .4;
  if (N.type === 'bewegung' || N.type === 'thema') g += .2;
  if (N.type === 'regional' && ['laendlich', 'kommunen'].some((i) => S.issues?.[i]?.on)) g += .3;
  if (N.id === 'XKL' && (S.crisisLog || []).some((c: any) => c.id === 'flut' && c.leg === S.leg)) g += .4;
  if (N.id === 'XGP' && (S.issues?.rente?.sev || 0) > 60) g += .3;
  const leg = (S.crisisLog || []).filter((c: any) => c.leg === S.leg).map((c: any) => c.id);
  if (N.id === 'XEU' && (leg.includes('zoll') || leg.includes('krieg'))) g += .5;
  const wo11 = (S as any).wo ?? 0; // Version 11: Ein starker Kurswechsel der Wirtschaftsordnung ruft Gegenparteien auf den Plan
  if (N.id === 'XWU' && wo11 <= -50) g += .5;
  if (N.id === 'XGF' && wo11 >= 50) g += .5;
  if (N.id === 'XTW' && (S.issues?.agrar?.sev || 0) > 50) g += .4;
  if (N.id === 'XKL' && (S.issues?.klima?.sev || 0) > 60) g += .4;
  if (N.id === 'XDF' && (S.laws || []).some((L: any) => (L.leg ?? 0) === S.leg && ['kraft', 'done'].includes(L.stage) && (L.fx?.pos?.innen || 0) > .5)) g += .6;
  return g;
}

/** Vollständiger Parteieintrag (alle Themen, Gewichte) aus der Vorlage. */
function build(N: NewParty, base: number): any {
  const axes = [...AX, ...AX3];
  const pos = Object.fromEntries(axes.map((a: string) => [a, N.pos[a] ?? 0]));
  const w = Object.fromEntries(axes.map((a: string) => [a, (N.core[a] ? 2.2 : N.pos[a] != null ? 1.2 : (EQW[a] ?? 1) * .6)]));
  return { n: N.n, a: N.a, c: N.c, base: Math.round(base * 10) / 10, prag: .78, pos, w, isNew: true, type: N.type, d: N.d, strong: N.strong };
}

/** Jährlich (Monat für Monat aufgerufen): vielleicht gründet sich eine neue Partei, neue Parteien folgen der Stimmung. */
export function partyTick(): boolean {
  if (!pxOn() || (S.phase !== 'govern' && S.phase !== 'opp')) return false; // Version 11: auch während der Opposition
  const P = px(); if (S.m < P.next) return false;
  P.next = S.m + 12;
  rivTick(P); // Version 12: Programme und Spitzenpersonal
  // Neue Parteien: Stärke folgt der Lücke
  for (const id of Object.keys(P.parties)) {
    const X = P.parties[id], N = NEW_PARTIES.find((n) => n.id === id); if (!N || P.gone[id] != null) continue;
    const pot = clamp(2 + gapOf(N) * 3, 1.5, 11);
    X.base = Math.round(clamp(X.base + (pot - X.base) * .25, 1, 14) * 10) / 10;
  }
  applyPx();
  const nComp = active().length; if (nComp >= MAX_PARTIES) return false;
  const cand = NEW_PARTIES.filter((N) => !P.parties[N.id]).map((N) => ({ N, g: gapOf(N) })).filter((x) => x.g > .35).sort((a, b) => b.g - a.g);
  if (!cand.length) return false;
  const pick = cand[Math.min(cand.length - 1, Math.floor(rnd() * Math.min(2, cand.length)))];
  if (rnd() >= clamp(.12 + pick.g * .3, 0, .55)) return false;
  found(pick.N, pick.g);
  return true;
}

function found(N: NewParty, g: number) {
  const P = px(), base = clamp(2.5 + g * 2.5, 2.5, 7.5);
  // Stimmen kommen vor allem von ähnlichen Parteien (und der Mutterpartei)
  const on = Object.keys(N.pos), donors = active().filter((k) => k !== N.id);
  P.parties[N.id] = build(N, base); P.born[N.id] = S.m; applyPx();
  const ws = donors.map((k) => aff(N.id, k, on) * (N.parent?.includes(k) ? 2.5 : 1));
  const sw = ws.reduce((a, b) => a + b, 0) || 1;
  donors.forEach((k, i) => { const take = base * .8 * ws[i] / sw; P.adj[k] = Math.round(((P.adj[k] || 0) - take) * 100) / 100; });
  const you = aff(N.id, 'YOU', on); if (you > .5) S.basis -= (you - .5) * base * .3;
  applyPx();
  const par = N.parent?.find((p) => PARTIES[p] && !PARTIES[p].gone);
  log(`Neue Partei: ${N.n} (${N.a}). ${N.d}${N.type === 'abspaltung' && par ? ` Ein Teil der Mitglieder kommt von ${PARTIES[par].n}.` : ''}`, 'bad');
  press(OUTLETS[0].n, `Neugründung: ${N.n} will in den Bundestag`, -.3);
}

/** Landtagswahl: Zusammensetzung des Landtags für alle Parteien. `you`: Ergebnis der eigenen Partei. */
export function landtag11(id: string, you: number) {
  if (!pxOn()) return;
  const P = px(), list = active();
  const raw: Record<string, number> = {}; let o = 0;
  for (const k of list) { const X = PARTIES[k]; const v = Math.max(.3, X.base) * ((X.strong || []).includes(id) ? 1.7 : 1) * (.75 + rnd() * .5); raw[k] = v; o += v; }
  const sh: Record<string, number> = { YOU: Math.round(you * 10) / 10 }, f = Math.max(0, 100 - you) / (o || 1);
  for (const k of list) sh[k] = Math.round(raw[k] * f * 10) / 10;
  const inn = Object.keys(sh).filter((k) => sh[k] >= 5);
  const before = P.lt[id]?.in || [];
  P.lt[id] = { m: S.m, in: inn, sh };
  const st = STATES[id].n;
  for (const k of list) {
    if (!PARTIES[k].isNew) continue;
    if (inn.includes(k) && !before.includes(k)) { P.parties[k].base = Math.round((P.parties[k].base + .8) * 10) / 10; log(`${PARTIES[k].n} zieht mit ${de(sh[k])} % in den Landtag von ${st} ein.`, 'bad'); press(OUTLETS[2]?.n || OUTLETS[0].n, `${PARTIES[k].a} erstmals im Landtag von ${st}`, -.2); }
    else if (!inn.includes(k) && before.includes(k)) { P.parties[k].base = Math.max(1, Math.round((P.parties[k].base - .3) * 10) / 10); log(`${PARTIES[k].n} fliegt aus dem Landtag von ${st}.`); }
  }
  applyPx();
}

/** Nach einer Bundestagswahl: Parteien, die dauerhaft scheitern, lösen sich auf. */
export function afterElection11(res: any) {
  if (!pxOn() || !res) return;
  const P = px(), govs = new Set(Object.values(S.gov || {}));
  for (const k of active()) {
    const sh = res.share[k] ?? 0;
    P.miss[k] = sh < 5 ? (P.miss[k] || 0) + 1 : 0;
    const inLt = Object.values(P.lt).some((x) => x.in.includes(k));
    if (P.miss[k] >= 2 && PARTIES[k].base < 12 && !inLt && !govs.has(k) && !S.partners.includes(k)) dissolve(k);
  }
}

function dissolve(k: string) {
  const P = px(), X = PARTIES[k], base = X.base, others = active().filter((x) => x !== k);
  P.gone[k] = S.m;
  const ws = others.map((x) => aff(k, x)), sw = ws.reduce((a, b) => a + b, 0) || 1;
  others.forEach((x, i) => { const add = base * ws[i] / sw; if (P.parties[x]) P.parties[x].base = Math.round((P.parties[x].base + add) * 10) / 10; else P.adj[x] = Math.round(((P.adj[x] || 0) + add) * 100) / 100; });
  applyPx();
  log(`${X.n} löst sich auf. Nach zwei verpassten Bundestagswahlen und ohne Sitz in einem Landtag geben die Mitglieder auf.`, 'good');
  press(OUTLETS[0].n, `Aus für ${X.a}: ${X.n} löst sich auf`, .2);
}

/** Für die Anzeige. */
export const isNewParty = (k: string): boolean => !!(S as any).px?.parties?.[k];
export const bornLabel = (k: string): number | null => ((S as any).px?.born?.[k] ?? null);
export const typeName = (k: string): string => TYPE_N[PARTIES[k]?.type as NewParty['type']] || '';
export const BASE_IDS = BASE_PARTIES;
